import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileSignature, Loader2, Search } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate, fmtDT } from "@/constants";
import { Input } from "@/components/ui/input";
import { ContractStatusChip } from "@/components/ContractSection";

const FILTERS = [
  ["tous", "Tous"],
  ["a_envoyer", "À envoyer"],
  ["en_attente", "En attente de signature"],
  ["en_retard", "En retard"],
  ["signes", "Signés"],
];

export default function Contracts() {
  const [tab, setTab] = useState("a_venir"); // a_venir | archives
  const [items, setItems] = useState(null);
  const [filter, setFilter] = useState("tous");
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setItems(null);
    api.get("/contracts", { params: { filtre: filter, periode: tab } })
      .then((r) => setItems(r.data))
      .catch(() => setItems([]));
  }, [filter, tab]);

  const q = query.trim().toLowerCase();
  const shown = tab === "archives" && q
    ? (items || []).filter((c) =>
        (c.client_name || "").toLowerCase().includes(q) ||
        (c.event_date || "").includes(q) ||
        fmtDate(c.event_date).toLowerCase().includes(q))
    : items;

  return (
    <div className="space-y-5" data-testid="contracts-page">
      <div>
        <h1 className="font-heading text-2xl font-bold tracking-tight flex items-center gap-2">
          <FileSignature className="h-6 w-6 text-slate-500" /> Contrats
        </h1>
        <p className="text-sm text-slate-500">Suivi des contrats de location : envoi, signature électronique, relances automatiques. Les contrats des prestations passées sont archivés automatiquement.</p>
      </div>

      <div className="flex gap-1.5 border-b border-slate-200" data-testid="contracts-tabs">
        {[["a_venir", "Contrats à venir"], ["archives", "Archives"]].map(([v, label]) => (
          <button key={v} data-testid={`contracts-tab-${v}`} onClick={() => setTab(v)}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${tab === v ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "archives" && (
        <div className="relative" data-testid="contracts-search-block">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input data-testid="contracts-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Rechercher un client ou une date (ex. 2026-09, 12 septembre)…" className="pl-9" />
        </div>
      )}

      <div className="flex flex-wrap gap-1.5" data-testid="contracts-filters">
        {FILTERS.map(([v, label]) => (
          <button key={v} data-testid={`contracts-filter-${v}`} onClick={() => setFilter(v)}
            className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${filter === v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:border-sky-400"}`}>
            {label}
          </button>
        ))}
      </div>

      {!shown ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : shown.length === 0 ? (
        <p className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500" data-testid="contracts-empty">
          {tab === "a_venir" ? "Aucun contrat à venir dans cette catégorie." : q ? "Aucune archive ne correspond à cette recherche." : "Aucun contrat archivé dans cette catégorie."}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="contracts-list">
          {shown.map((c, i) => (
            <li key={c.id}>
              <button data-testid={`contracts-item-${i}`} onClick={() => navigate(`/reservations/${c.reservation_id}`)}
                className="w-full text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-sky-300 transition-colors">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm">{c.client_name}</span>
                  <ContractStatusChip status={c.display_status} />
                  {tab === "archives" && <span className="rounded bg-slate-100 text-slate-500 px-1.5 py-0.5 text-[9px] font-bold uppercase">Archivé</span>}
                  <span className="ml-auto text-xs text-slate-500">{c.event_date ? fmtDate(c.event_date) : "—"}</span>
                </div>
                <p className="text-xs text-slate-500 mt-1">{c.event_type} · {c.materials_label}</p>
                <div className="flex gap-4 mt-1.5 text-xs text-slate-400 flex-wrap">
                  <span>Dernier envoi : {c.sent_at ? fmtDT(c.sent_at) : "—"}</span>
                  {c.next_reminder_at && c.display_status !== "signe" && (
                    <span>Prochaine relance : {fmtDate(c.next_reminder_at.slice(0, 10))}</span>
                  )}
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
